import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from './AdminShell';
import { api } from '../../lib/api';
import { Plus, Edit3, Trash2, Eye, EyeOff, Star, Sparkles } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/blog');
      setPosts(res.data);
    } catch (e) {
      // silent
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!confirm('Delete this post?')) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      toast({ title: 'Post deleted' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const toggleFeature = async (post) => {
    try {
      if (post.featured) {
        await api.post(`/admin/blog/${post.id}/unfeature`);
        toast({ title: 'Removed from featured' });
      } else {
        await api.post(`/admin/blog/${post.id}/feature`);
        toast({
          title: 'Featured on Blog',
          description: 'This post now appears at the top of the Blog page. Any previous featured post has been unfeatured.',
        });
      }
      load();
    } catch (e) {
      toast({ title: 'Could not update featured', variant: 'destructive' });
    }
  };

  return (
    <AdminShell title="Blog posts">
      <div className="flex justify-between items-center mb-4">
        <div className="text-xs text-ink-3">
          Tip: click the star to pin a post as the large featured card on the public Blog page. Only one post can be featured at a time.
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/workflows/new" className="btn-secondary" data-testid="new-post-ai-workflow-btn">
            <Sparkles className="w-4 h-4" /> New post with AI workflow
          </Link>
          <Link to="/admin/blog/new" className="btn-primary" data-testid="new-post-btn">
            <Plus className="w-4 h-4" /> New post
          </Link>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-[var(--line-2)] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-ink-3 text-sm">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="p-8 text-center text-ink-3 text-sm">No posts yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-alt)] text-ink-3 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3 w-10"></th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Slug</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Updated</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className={`border-t border-[var(--line-2)] ${p.featured ? 'bg-amber-50/40' : ''}`}>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleFeature(p)}
                      title={p.featured ? 'Unfeature' : 'Feature on Blog page'}
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                        p.featured ? 'text-amber-500 bg-amber-100' : 'text-ink-3 hover:text-amber-500 hover:bg-amber-50'
                      }`}
                    >
                      <Star className={`w-4 h-4 ${p.featured ? 'fill-current' : ''}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-ink font-medium">
                    {p.title}
                    {p.featured && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-amber-600 align-middle">
                        <Star className="w-2.5 h-2.5 fill-current" /> Featured
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-2">/{p.slug}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${
                        p.published ? 'bg-emerald-50 text-emerald-700' : 'bg-[var(--bg-alt)] text-ink-3'
                      }`}
                    >
                      {p.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {p.published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-3">{new Date(p.updated_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Link to={`/admin/blog/${p.id}`} className="text-ink-2 hover:text-ink" title="Edit">
                        <Edit3 className="w-4 h-4" />
                      </Link>
                      <button onClick={() => remove(p.id)} className="text-ink-2 hover:text-red-600" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminBlog;
